/**
 * useMatrixModals Hook
 * Consolidates all modal/dialog state management for MatrixPage
 * Uses useReducer to manage ~20+ related state variables in one place
 */

import { useReducer, useCallback, useMemo } from 'react';

// Action types
const ACTIONS = {
  // Cell selection dialog
  OPEN_CELL_DIALOG: 'OPEN_CELL_DIALOG',
  CLOSE_CELL_DIALOG: 'CLOSE_CELL_DIALOG',

  // Action modals (upload, booking, quote, details, remove)
  OPEN_UPLOAD_MODAL: 'OPEN_UPLOAD_MODAL',
  CLOSE_UPLOAD_MODAL: 'CLOSE_UPLOAD_MODAL',
  OPEN_BOOKING_MODAL: 'OPEN_BOOKING_MODAL',
  CLOSE_BOOKING_MODAL: 'CLOSE_BOOKING_MODAL',
  OPEN_QUOTE_MODAL: 'OPEN_QUOTE_MODAL',
  CLOSE_QUOTE_MODAL: 'CLOSE_QUOTE_MODAL',
  OPEN_DETAILS_MODAL: 'OPEN_DETAILS_MODAL',
  CLOSE_DETAILS_MODAL: 'CLOSE_DETAILS_MODAL',
  OPEN_REMOVE_MODAL: 'OPEN_REMOVE_MODAL',
  CLOSE_REMOVE_MODAL: 'CLOSE_REMOVE_MODAL',

  // Cross-space modal
  OPEN_CROSS_SPACE_MODAL: 'OPEN_CROSS_SPACE_MODAL',
  CLOSE_CROSS_SPACE_MODAL: 'CLOSE_CROSS_SPACE_MODAL',

  // Add member/qualification dialogs
  OPEN_ADD_MEMBER_DIALOG: 'OPEN_ADD_MEMBER_DIALOG',
  CLOSE_ADD_MEMBER_DIALOG: 'CLOSE_ADD_MEMBER_DIALOG',
  OPEN_ADD_QUALIFICATION_DIALOG: 'OPEN_ADD_QUALIFICATION_DIALOG',
  CLOSE_ADD_QUALIFICATION_DIALOG: 'CLOSE_ADD_QUALIFICATION_DIALOG',
  OPEN_ADD_QUALIFICATION_MODAL: 'OPEN_ADD_QUALIFICATION_MODAL',
  CLOSE_ADD_QUALIFICATION_MODAL: 'CLOSE_ADD_QUALIFICATION_MODAL',

  // Add members to qualification dialog
  OPEN_ADD_MEMBERS_DIALOG: 'OPEN_ADD_MEMBERS_DIALOG',
  CLOSE_ADD_MEMBERS_DIALOG: 'CLOSE_ADD_MEMBERS_DIALOG',

  // Delete view dialog
  OPEN_DELETE_VIEW_DIALOG: 'OPEN_DELETE_VIEW_DIALOG',
  CLOSE_DELETE_VIEW_DIALOG: 'CLOSE_DELETE_VIEW_DIALOG',

  // Context menus
  OPEN_CONTEXT_MENU: 'OPEN_CONTEXT_MENU',
  CLOSE_CONTEXT_MENU: 'CLOSE_CONTEXT_MENU',
  OPEN_HEADER_CONTEXT_MENU: 'OPEN_HEADER_CONTEXT_MENU',
  CLOSE_HEADER_CONTEXT_MENU: 'CLOSE_HEADER_CONTEXT_MENU',

  // Certificates modal
  OPEN_CERTIFICATES_MODAL: 'OPEN_CERTIFICATES_MODAL',
  CLOSE_CERTIFICATES_MODAL: 'CLOSE_CERTIFICATES_MODAL',

  // Space selector
  OPEN_SPACE_SELECTOR: 'OPEN_SPACE_SELECTOR',
  CLOSE_SPACE_SELECTOR: 'CLOSE_SPACE_SELECTOR',

  // Validation warning
  SET_VALIDATION_WARNING: 'SET_VALIDATION_WARNING',
  CLEAR_VALIDATION_WARNING: 'CLEAR_VALIDATION_WARNING',

  // Dock (radial action menu)
  OPEN_DOCK: 'OPEN_DOCK',
  CLOSE_DOCK: 'CLOSE_DOCK',

  // Dropdowns
  TOGGLE_HIDE_FIELDS: 'TOGGLE_HIDE_FIELDS',
  CLOSE_HIDE_FIELDS: 'CLOSE_HIDE_FIELDS',
  TOGGLE_FILTER: 'TOGGLE_FILTER',
  CLOSE_FILTER: 'CLOSE_FILTER',

  // Smart upload confirmation
  SET_SMART_UPLOAD_CONFIRMATION: 'SET_SMART_UPLOAD_CONFIRMATION',
  CLEAR_SMART_UPLOAD_CONFIRMATION: 'CLEAR_SMART_UPLOAD_CONFIRMATION',

  // Bulk upload
  SET_BULK_UPLOAD_CONFIRMATION: 'SET_BULK_UPLOAD_CONFIRMATION',
  CLEAR_BULK_UPLOAD_CONFIRMATION: 'CLEAR_BULK_UPLOAD_CONFIRMATION',
  SET_BULK_UPLOAD_RESULTS: 'SET_BULK_UPLOAD_RESULTS',
  CLEAR_BULK_UPLOAD_RESULTS: 'CLEAR_BULK_UPLOAD_RESULTS',

  // Close all modals
  CLOSE_ALL: 'CLOSE_ALL',
};

// Initial state
const initialState = {
  // Cell selection
  selectedCell: null,
  dialogOpen: false,

  // Action modals
  uploadModalOpen: false,
  bookingModalOpen: false,
  quoteModalOpen: false,
  quoteDetails: null,
  detailsModalOpen: false,
  removeModalOpen: false,
  crossSpaceModalOpen: false,

  // Add dialogs
  addMemberDialogOpen: false,
  addQualificationDialogOpen: false,
  isAddQualificationModalOpen: false,
  addMembersDialogOpen: false,
  selectedQualificationForAdd: null,

  // Delete view
  deleteViewDialogOpen: false,
  viewToDelete: null,

  // Context menus
  contextMenuOpen: false,
  contextMenuPosition: { x: 0, y: 0 },
  contextMenuCell: null,
  headerContextMenuOpen: false,
  headerContextMenuPosition: { x: 0, y: 0 },
  headerContextMenuQual: null,

  // Certificates
  certificatesModalOpen: false,
  selectedMemberCertificates: null,

  // Space selector
  spaceSelectorOpen: false,
  pendingAssignment: null,

  // Validation
  validationWarning: null,

  // Dock
  dockOpen: false,
  dockPosition: { x: 0, y: 0 },
  dockCell: null,

  // Dropdowns
  hideFieldsOpen: false,
  filterOpen: false,

  // Smart upload
  smartUploadConfirmation: null,

  // Bulk upload
  bulkUploadConfirmation: null,
  bulkUploadResults: null,
};

// Reducer function
function modalReducer(state, action) {
  switch (action.type) {
    // Cell selection dialog
    case ACTIONS.OPEN_CELL_DIALOG:
      return {
        ...state,
        selectedCell: action.payload,
        dialogOpen: true,
      };
    case ACTIONS.CLOSE_CELL_DIALOG:
      return {
        ...state,
        dialogOpen: false,
        // Keep selectedCell for potential use after close
      };

    // Upload modal
    case ACTIONS.OPEN_UPLOAD_MODAL:
      return { ...state, uploadModalOpen: true, dialogOpen: false };
    case ACTIONS.CLOSE_UPLOAD_MODAL:
      return { ...state, uploadModalOpen: false };

    // Booking modal
    case ACTIONS.OPEN_BOOKING_MODAL:
      return { ...state, bookingModalOpen: true, dialogOpen: false };
    case ACTIONS.CLOSE_BOOKING_MODAL:
      return { ...state, bookingModalOpen: false };

    // Quote modal
    case ACTIONS.OPEN_QUOTE_MODAL:
      return {
        ...state,
        quoteModalOpen: true,
        quoteDetails: action.payload || null,
        dialogOpen: false,
      };
    case ACTIONS.CLOSE_QUOTE_MODAL:
      return { ...state, quoteModalOpen: false, quoteDetails: null };

    // Details modal
    case ACTIONS.OPEN_DETAILS_MODAL:
      return { ...state, detailsModalOpen: true, dialogOpen: false };
    case ACTIONS.CLOSE_DETAILS_MODAL:
      return { ...state, detailsModalOpen: false };

    // Remove modal
    case ACTIONS.OPEN_REMOVE_MODAL:
      return { ...state, removeModalOpen: true, dialogOpen: false };
    case ACTIONS.CLOSE_REMOVE_MODAL:
      return { ...state, removeModalOpen: false };

    // Cross-space modal
    case ACTIONS.OPEN_CROSS_SPACE_MODAL:
      return { ...state, crossSpaceModalOpen: true, dialogOpen: false };
    case ACTIONS.CLOSE_CROSS_SPACE_MODAL:
      return { ...state, crossSpaceModalOpen: false };

    // Add member dialog
    case ACTIONS.OPEN_ADD_MEMBER_DIALOG:
      return { ...state, addMemberDialogOpen: true };
    case ACTIONS.CLOSE_ADD_MEMBER_DIALOG:
      return { ...state, addMemberDialogOpen: false };

    // Add qualification dialog
    case ACTIONS.OPEN_ADD_QUALIFICATION_DIALOG:
      return { ...state, addQualificationDialogOpen: true };
    case ACTIONS.CLOSE_ADD_QUALIFICATION_DIALOG:
      return { ...state, addQualificationDialogOpen: false };

    // Add qualification modal (full modal)
    case ACTIONS.OPEN_ADD_QUALIFICATION_MODAL:
      return { ...state, isAddQualificationModalOpen: true };
    case ACTIONS.CLOSE_ADD_QUALIFICATION_MODAL:
      return { ...state, isAddQualificationModalOpen: false };

    // Add members to qualification dialog
    case ACTIONS.OPEN_ADD_MEMBERS_DIALOG:
      return {
        ...state,
        addMembersDialogOpen: true,
        selectedQualificationForAdd: action.payload,
      };
    case ACTIONS.CLOSE_ADD_MEMBERS_DIALOG:
      return {
        ...state,
        addMembersDialogOpen: false,
        selectedQualificationForAdd: null,
      };

    // Delete view dialog
    case ACTIONS.OPEN_DELETE_VIEW_DIALOG:
      return {
        ...state,
        deleteViewDialogOpen: true,
        viewToDelete: action.payload,
      };
    case ACTIONS.CLOSE_DELETE_VIEW_DIALOG:
      return {
        ...state,
        deleteViewDialogOpen: false,
        viewToDelete: null,
      };

    // Context menu
    case ACTIONS.OPEN_CONTEXT_MENU:
      return {
        ...state,
        contextMenuOpen: true,
        contextMenuPosition: action.payload.position,
        contextMenuCell: action.payload.cell,
      };
    case ACTIONS.CLOSE_CONTEXT_MENU:
      return {
        ...state,
        contextMenuOpen: false,
      };

    // Header context menu
    case ACTIONS.OPEN_HEADER_CONTEXT_MENU:
      return {
        ...state,
        headerContextMenuOpen: true,
        headerContextMenuPosition: action.payload.position,
        headerContextMenuQual: action.payload.qual,
      };
    case ACTIONS.CLOSE_HEADER_CONTEXT_MENU:
      return {
        ...state,
        headerContextMenuOpen: false,
      };

    // Certificates modal
    case ACTIONS.OPEN_CERTIFICATES_MODAL:
      return {
        ...state,
        certificatesModalOpen: true,
        selectedMemberCertificates: action.payload,
      };
    case ACTIONS.CLOSE_CERTIFICATES_MODAL:
      return {
        ...state,
        certificatesModalOpen: false,
        selectedMemberCertificates: null,
      };

    // Space selector
    case ACTIONS.OPEN_SPACE_SELECTOR:
      return {
        ...state,
        spaceSelectorOpen: true,
        pendingAssignment: action.payload,
      };
    case ACTIONS.CLOSE_SPACE_SELECTOR:
      return {
        ...state,
        spaceSelectorOpen: false,
        pendingAssignment: null,
      };

    // Validation warning
    case ACTIONS.SET_VALIDATION_WARNING:
      return { ...state, validationWarning: action.payload };
    case ACTIONS.CLEAR_VALIDATION_WARNING:
      return { ...state, validationWarning: null };

    // Dock
    case ACTIONS.OPEN_DOCK:
      return {
        ...state,
        dockOpen: true,
        dockPosition: action.payload.position,
        dockCell: action.payload.cell,
        dialogOpen: false, // Close the cell dialog when opening dock
      };
    case ACTIONS.CLOSE_DOCK:
      return {
        ...state,
        dockOpen: false,
      };

    // Hide fields dropdown
    case ACTIONS.TOGGLE_HIDE_FIELDS:
      return { ...state, hideFieldsOpen: !state.hideFieldsOpen, filterOpen: false };
    case ACTIONS.CLOSE_HIDE_FIELDS:
      return { ...state, hideFieldsOpen: false };

    // Filter dropdown
    case ACTIONS.TOGGLE_FILTER:
      return { ...state, filterOpen: !state.filterOpen, hideFieldsOpen: false };
    case ACTIONS.CLOSE_FILTER:
      return { ...state, filterOpen: false };

    // Smart upload confirmation
    case ACTIONS.SET_SMART_UPLOAD_CONFIRMATION:
      return { ...state, smartUploadConfirmation: action.payload };
    case ACTIONS.CLEAR_SMART_UPLOAD_CONFIRMATION:
      return { ...state, smartUploadConfirmation: null };

    // Bulk upload
    case ACTIONS.SET_BULK_UPLOAD_CONFIRMATION:
      return { ...state, bulkUploadConfirmation: action.payload };
    case ACTIONS.CLEAR_BULK_UPLOAD_CONFIRMATION:
      return { ...state, bulkUploadConfirmation: false };
    case ACTIONS.SET_BULK_UPLOAD_RESULTS:
      return { ...state, bulkUploadResults: action.payload };
    case ACTIONS.CLEAR_BULK_UPLOAD_RESULTS:
      return { ...state, bulkUploadResults: null };

    // Close all
    case ACTIONS.CLOSE_ALL:
      return initialState;

    default:
      return state;
  }
}

/**
 * Custom hook for managing all matrix modal/dialog state
 * @returns {Object} Modal state and action dispatchers
 */
export function useMatrixModals() {
  const [state, dispatch] = useReducer(modalReducer, initialState);

  // Memoized action creators for better performance
  const actions = useMemo(() => ({
    // Cell dialog
    openCellDialog: (cell) => dispatch({ type: ACTIONS.OPEN_CELL_DIALOG, payload: cell }),
    closeCellDialog: () => dispatch({ type: ACTIONS.CLOSE_CELL_DIALOG }),

    // Action modals
    openUploadModal: () => dispatch({ type: ACTIONS.OPEN_UPLOAD_MODAL }),
    closeUploadModal: () => dispatch({ type: ACTIONS.CLOSE_UPLOAD_MODAL }),
    openBookingModal: () => dispatch({ type: ACTIONS.OPEN_BOOKING_MODAL }),
    closeBookingModal: () => dispatch({ type: ACTIONS.CLOSE_BOOKING_MODAL }),
    openQuoteModal: (details) => dispatch({ type: ACTIONS.OPEN_QUOTE_MODAL, payload: details }),
    closeQuoteModal: () => dispatch({ type: ACTIONS.CLOSE_QUOTE_MODAL }),
    openDetailsModal: () => dispatch({ type: ACTIONS.OPEN_DETAILS_MODAL }),
    closeDetailsModal: () => dispatch({ type: ACTIONS.CLOSE_DETAILS_MODAL }),
    openRemoveModal: () => dispatch({ type: ACTIONS.OPEN_REMOVE_MODAL }),
    closeRemoveModal: () => dispatch({ type: ACTIONS.CLOSE_REMOVE_MODAL }),
    openCrossSpaceModal: () => dispatch({ type: ACTIONS.OPEN_CROSS_SPACE_MODAL }),
    closeCrossSpaceModal: () => dispatch({ type: ACTIONS.CLOSE_CROSS_SPACE_MODAL }),

    // Add dialogs
    openAddMemberDialog: () => dispatch({ type: ACTIONS.OPEN_ADD_MEMBER_DIALOG }),
    closeAddMemberDialog: () => dispatch({ type: ACTIONS.CLOSE_ADD_MEMBER_DIALOG }),
    openAddQualificationDialog: () => dispatch({ type: ACTIONS.OPEN_ADD_QUALIFICATION_DIALOG }),
    closeAddQualificationDialog: () => dispatch({ type: ACTIONS.CLOSE_ADD_QUALIFICATION_DIALOG }),
    openAddQualificationModal: () => dispatch({ type: ACTIONS.OPEN_ADD_QUALIFICATION_MODAL }),
    closeAddQualificationModal: () => dispatch({ type: ACTIONS.CLOSE_ADD_QUALIFICATION_MODAL }),
    openAddMembersDialog: (qual) => dispatch({ type: ACTIONS.OPEN_ADD_MEMBERS_DIALOG, payload: qual }),
    closeAddMembersDialog: () => dispatch({ type: ACTIONS.CLOSE_ADD_MEMBERS_DIALOG }),

    // Delete view
    openDeleteViewDialog: (view) => dispatch({ type: ACTIONS.OPEN_DELETE_VIEW_DIALOG, payload: view }),
    closeDeleteViewDialog: () => dispatch({ type: ACTIONS.CLOSE_DELETE_VIEW_DIALOG }),

    // Context menus
    openContextMenu: (position, cell) => dispatch({
      type: ACTIONS.OPEN_CONTEXT_MENU,
      payload: { position, cell }
    }),
    closeContextMenu: () => dispatch({ type: ACTIONS.CLOSE_CONTEXT_MENU }),
    openHeaderContextMenu: (position, qual) => dispatch({
      type: ACTIONS.OPEN_HEADER_CONTEXT_MENU,
      payload: { position, qual }
    }),
    closeHeaderContextMenu: () => dispatch({ type: ACTIONS.CLOSE_HEADER_CONTEXT_MENU }),

    // Certificates
    openCertificatesModal: (memberData) => dispatch({
      type: ACTIONS.OPEN_CERTIFICATES_MODAL,
      payload: memberData
    }),
    closeCertificatesModal: () => dispatch({ type: ACTIONS.CLOSE_CERTIFICATES_MODAL }),

    // Space selector
    openSpaceSelector: (assignment) => dispatch({
      type: ACTIONS.OPEN_SPACE_SELECTOR,
      payload: assignment
    }),
    closeSpaceSelector: () => dispatch({ type: ACTIONS.CLOSE_SPACE_SELECTOR }),

    // Validation
    setValidationWarning: (warning) => dispatch({
      type: ACTIONS.SET_VALIDATION_WARNING,
      payload: warning
    }),
    clearValidationWarning: () => dispatch({ type: ACTIONS.CLEAR_VALIDATION_WARNING }),

    // Dock
    openDock: (position, cell) => dispatch({
      type: ACTIONS.OPEN_DOCK,
      payload: { position, cell }
    }),
    closeDock: () => dispatch({ type: ACTIONS.CLOSE_DOCK }),

    // Dropdowns
    toggleHideFields: () => dispatch({ type: ACTIONS.TOGGLE_HIDE_FIELDS }),
    closeHideFields: () => dispatch({ type: ACTIONS.CLOSE_HIDE_FIELDS }),
    toggleFilter: () => dispatch({ type: ACTIONS.TOGGLE_FILTER }),
    closeFilter: () => dispatch({ type: ACTIONS.CLOSE_FILTER }),

    // Smart upload
    setSmartUploadConfirmation: (data) => dispatch({
      type: ACTIONS.SET_SMART_UPLOAD_CONFIRMATION,
      payload: data
    }),
    clearSmartUploadConfirmation: () => dispatch({ type: ACTIONS.CLEAR_SMART_UPLOAD_CONFIRMATION }),

    // Bulk upload
    setBulkUploadConfirmation: (data) => dispatch({
      type: ACTIONS.SET_BULK_UPLOAD_CONFIRMATION,
      payload: data
    }),
    clearBulkUploadConfirmation: () => dispatch({ type: ACTIONS.CLEAR_BULK_UPLOAD_CONFIRMATION }),
    setBulkUploadResults: (data) => dispatch({
      type: ACTIONS.SET_BULK_UPLOAD_RESULTS,
      payload: data
    }),
    clearBulkUploadResults: () => dispatch({ type: ACTIONS.CLEAR_BULK_UPLOAD_RESULTS }),

    // Close all
    closeAll: () => dispatch({ type: ACTIONS.CLOSE_ALL }),
  }), []);

  // Setter-style functions for backward compatibility
  // These allow gradual migration from useState to the reducer
  const setters = useMemo(() => ({
    setSelectedCell: (cell) => {
      if (cell === null) {
        dispatch({ type: ACTIONS.CLOSE_CELL_DIALOG });
      } else {
        dispatch({ type: ACTIONS.OPEN_CELL_DIALOG, payload: cell });
      }
    },
    setDialogOpen: (open) => {
      if (open) {
        // Can't open without a cell, this is a noop
      } else {
        dispatch({ type: ACTIONS.CLOSE_CELL_DIALOG });
      }
    },
    setUploadModalOpen: (open) => dispatch({
      type: open ? ACTIONS.OPEN_UPLOAD_MODAL : ACTIONS.CLOSE_UPLOAD_MODAL
    }),
    setBookingModalOpen: (open) => dispatch({
      type: open ? ACTIONS.OPEN_BOOKING_MODAL : ACTIONS.CLOSE_BOOKING_MODAL
    }),
    setQuoteModalOpen: (open) => dispatch({
      type: open ? ACTIONS.OPEN_QUOTE_MODAL : ACTIONS.CLOSE_QUOTE_MODAL
    }),
    setDetailsModalOpen: (open) => dispatch({
      type: open ? ACTIONS.OPEN_DETAILS_MODAL : ACTIONS.CLOSE_DETAILS_MODAL
    }),
    setRemoveModalOpen: (open) => dispatch({
      type: open ? ACTIONS.OPEN_REMOVE_MODAL : ACTIONS.CLOSE_REMOVE_MODAL
    }),
    setCrossSpaceModalOpen: (open) => dispatch({
      type: open ? ACTIONS.OPEN_CROSS_SPACE_MODAL : ACTIONS.CLOSE_CROSS_SPACE_MODAL
    }),
    setAddMemberDialogOpen: (open) => dispatch({
      type: open ? ACTIONS.OPEN_ADD_MEMBER_DIALOG : ACTIONS.CLOSE_ADD_MEMBER_DIALOG
    }),
    setAddQualificationDialogOpen: (open) => dispatch({
      type: open ? ACTIONS.OPEN_ADD_QUALIFICATION_DIALOG : ACTIONS.CLOSE_ADD_QUALIFICATION_DIALOG
    }),
    setIsAddQualificationModalOpen: (open) => dispatch({
      type: open ? ACTIONS.OPEN_ADD_QUALIFICATION_MODAL : ACTIONS.CLOSE_ADD_QUALIFICATION_MODAL
    }),
    setAddMembersDialogOpen: (open) => {
      if (!open) dispatch({ type: ACTIONS.CLOSE_ADD_MEMBERS_DIALOG });
      // Opening requires qualification, use openAddMembersDialog instead
    },
    setDeleteViewDialogOpen: (open) => {
      if (!open) dispatch({ type: ACTIONS.CLOSE_DELETE_VIEW_DIALOG });
      // Opening requires view, use openDeleteViewDialog instead
    },
    setContextMenuOpen: (open) => {
      if (!open) dispatch({ type: ACTIONS.CLOSE_CONTEXT_MENU });
    },
    setHeaderContextMenuOpen: (open) => {
      if (!open) dispatch({ type: ACTIONS.CLOSE_HEADER_CONTEXT_MENU });
    },
    setCertificatesModalOpen: (open) => {
      if (!open) dispatch({ type: ACTIONS.CLOSE_CERTIFICATES_MODAL });
    },
    setSpaceSelectorOpen: (open) => {
      if (!open) dispatch({ type: ACTIONS.CLOSE_SPACE_SELECTOR });
    },
    setValidationWarning: (warning) => dispatch({
      type: warning ? ACTIONS.SET_VALIDATION_WARNING : ACTIONS.CLEAR_VALIDATION_WARNING,
      payload: warning
    }),
    setDockOpen: (open) => {
      if (!open) dispatch({ type: ACTIONS.CLOSE_DOCK });
    },
    setHideFieldsOpen: (open) => {
      if (open) {
        dispatch({ type: ACTIONS.TOGGLE_HIDE_FIELDS });
      } else {
        dispatch({ type: ACTIONS.CLOSE_HIDE_FIELDS });
      }
    },
    setFilterOpen: (open) => {
      if (open) {
        dispatch({ type: ACTIONS.TOGGLE_FILTER });
      } else {
        dispatch({ type: ACTIONS.CLOSE_FILTER });
      }
    },
    setSmartUploadConfirmation: (data) => dispatch({
      type: data ? ACTIONS.SET_SMART_UPLOAD_CONFIRMATION : ACTIONS.CLEAR_SMART_UPLOAD_CONFIRMATION,
      payload: data
    }),
    setBulkUploadConfirmation: (data) => dispatch({
      type: data ? ACTIONS.SET_BULK_UPLOAD_CONFIRMATION : ACTIONS.CLEAR_BULK_UPLOAD_CONFIRMATION,
      payload: data
    }),
    setBulkUploadResults: (data) => dispatch({
      type: data ? ACTIONS.SET_BULK_UPLOAD_RESULTS : ACTIONS.CLEAR_BULK_UPLOAD_RESULTS,
      payload: data
    }),
    setQuoteDetails: (details) => {
      // This is a data setter, update state manually if quote modal is open
      if (state.quoteModalOpen) {
        dispatch({ type: ACTIONS.OPEN_QUOTE_MODAL, payload: details });
      }
    },
    setViewToDelete: (view) => {
      if (view) {
        dispatch({ type: ACTIONS.OPEN_DELETE_VIEW_DIALOG, payload: view });
      }
    },
    setSelectedQualificationForAdd: (qual) => {
      if (qual) {
        dispatch({ type: ACTIONS.OPEN_ADD_MEMBERS_DIALOG, payload: qual });
      }
    },
    setContextMenuPosition: () => {}, // Handled by openContextMenu
    setContextMenuCell: () => {}, // Handled by openContextMenu
    setHeaderContextMenuPosition: () => {}, // Handled by openHeaderContextMenu
    setHeaderContextMenuQual: () => {}, // Handled by openHeaderContextMenu
    setSelectedMemberCertificates: (data) => {
      if (data) {
        dispatch({ type: ACTIONS.OPEN_CERTIFICATES_MODAL, payload: data });
      }
    },
    setPendingAssignment: (assignment) => {
      if (assignment) {
        dispatch({ type: ACTIONS.OPEN_SPACE_SELECTOR, payload: assignment });
      }
    },
    setDockPosition: () => {}, // Handled by openDock
    setDockCell: () => {}, // Handled by openDock
  }), [state.quoteModalOpen]);

  return {
    // State
    ...state,

    // Action creators (preferred API)
    ...actions,

    // Setter functions (backward compatibility)
    ...setters,
  };
}

// Export action types for testing
export { ACTIONS };

export default useMatrixModals;
